import type { QueryKey } from '@tanstack/react-query';

import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../components/generic/Pagination/consts';
import usePaginationState from '../../../components/generic/Pagination/usePaginationState';
import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { GetTeamProjectsParams, GetTeamProjectsResponse } from '../../../../shared/types/routes/teams';

/**
 * Function to fetch team projects
 */
function fetchTeamProjects(teamId: number, params: GetTeamProjectsParams) {
  const searchParams = new URLSearchParams({
    page: (params?.page ?? DEFAULT_PAGE).toString(),
    pageSize: (params?.pageSize ?? DEFAULT_PAGE_SIZE).toString(),
  });

  if (params.search) {
    searchParams.append('search', params.search);
  }

  if (params.sort) {
    searchParams.append('sort', params.sort);
  }

  if (params.skipPagination) {
    searchParams.append('skipPagination', 'true');
  }

  return get<GetTeamProjectsResponse>(`/api/teams/${teamId}/projects?${searchParams.toString()}`);
}

/**
 * Base query key for team projects query. This can be used to invalidate ALL pages
 * of team projects for a given team.
 */
export const getBaseTeamProjectsQueryKey = (teamId: number): QueryKey =>
  ['teams', teamId, 'projects'];

/**
 * Full query key for team projects query
 */
export const getTeamProjectsQueryKey = (teamId: number, params: GetTeamProjectsParams): QueryKey =>
  [...getBaseTeamProjectsQueryKey(teamId), params];

/**
 * Hook for fetching team projects. Bring your own pagination state.
 *
 * @param {number} teamId - ID of the team to fetch projects for
 * @param {GetTeamProjectsParams} params - Pagination and search parameters
 * @param {boolean} shouldFetch - Whether the query should automatically run
 */
export function useTeamProjects(
  teamId: number,
  params: GetTeamProjectsParams,
  shouldFetch: boolean = true,
) {
  return useQuery<GetTeamProjectsResponse>({
    queryKey: getTeamProjectsQueryKey(teamId, params),
    queryFn: () => fetchTeamProjects(teamId, params),
    enabled: shouldFetch,
    // Keeps previous data visible while fetching next page
    keepPreviousData: true,
  });
}

const DEFAULT_INITIAL_PARAMS: GetTeamProjectsParams = {
  search: '',
  page: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  sort: 'asc',
  skipPagination: false,
};

/**
 * Hook for fetching team projects with pagination and optional search.
 */
export default function usePaginatedTeamProjects(
  teamId: number,
  params: Partial<GetTeamProjectsParams> = DEFAULT_INITIAL_PARAMS,
  shouldFetch: boolean = true,
) {
  const { page, pageSize, setPage, setPageSize } = usePaginationState(params.page, params.pageSize);
  const query = useTeamProjects(teamId, { page, pageSize, search: params.search, sort: params.sort ?? 'asc', skipPagination: !!params.skipPagination }, shouldFetch);

  return {
    ...query,
    page,
    pageSize,
    setPage,
    setPageSize,
  };
}
